import { ApolloClient, NormalizedCacheObject, useMutation, useQuery } from '@apollo/client';
import {
  JoinGroupByCodeData,
  JoinGroupByCodeVars,
  JoinGroupData,
  JoinGroupVars,
  PublicGroupsData,
  PublicGroupsVars
} from '../../group/gql-types';

import ApolloClientService from '../../shared/services/ApolloClientService';
import Config from '../../shared/config';
import GraphQL from '../gql';

let dashboardApolloClient: ApolloClient<NormalizedCacheObject> = null;
let userGroupApolloClient: ApolloClient<NormalizedCacheObject> = null;

function usePublicGroups() {
  if (!dashboardApolloClient) {
    dashboardApolloClient = ApolloClientService.create(Config.DASHBOARD_GQL);
  }

  if (!userGroupApolloClient) {
    userGroupApolloClient = ApolloClientService.create(Config.GROUP_GQL);
  }

  const { loading, error, data } = useQuery<PublicGroupsData, PublicGroupsVars>(
    GraphQL.Query.PUBLIC_GROUPS,
    {
      client: userGroupApolloClient,
      fetchPolicy: 'cache-and-network'
    }
  );

  const [_joinGroup, { loading: joining, error: joinError, data: joinData }] =
    useMutation<JoinGroupData, JoinGroupVars>(GraphQL.Mutation.JOIN_GROUP, {
      client: userGroupApolloClient,
      refetchQueries: [
        {
          query: GraphQL.Query.PUBLIC_GROUPS
        }
      ]
    });

  const joinGroup = async (
    userkey: number,
    groupkey: number
  ): Promise<void> => {
    await _joinGroup({
      variables: {
        joinGroupParams: {
          userkey,
          groupkey
        }
      }
    });
  };

  const [
    _joinGroupByCode,
    { loading: joiningCode, error: joinCodeError, data: joinCodeData }
  ] = useMutation<JoinGroupByCodeData, JoinGroupByCodeVars>(
    GraphQL.Mutation.JOIN_GROUP_BY_CODE,
    {
      client: userGroupApolloClient,
      refetchQueries: [
        {
          query: GraphQL.Query.PUBLIC_GROUPS
        }
      ]
    }
  );

  const joinGroupByCode = async (
    userkey: number,
    groupkey: number,
    code: string
  ): Promise<void> => {
    await _joinGroupByCode({
      variables: {
        joinGroupByCodeParams: {
          userkey,
          groupkey,
          code
        }
      }
    });
  };

  return {
    loading,
    error: !!error,
    publicGroups: data?.publicGroups,
    joinGroup,
    joining,
    joinError: !!joinError,
    joined: joinData?.joinGroup,
    joinGroupByCode,
    joiningCode,
    joinCodeError: !!joinCodeError,
    joinedByCode: joinCodeData?.joinGroupByCode
  };
}

export default usePublicGroups;
